import type { Manifest } from "./manifest";

/**
 * What a page's post may hold (docs/views.md#6-the-page-api): the most of its text, of the people it mentions, and of an
 * update's summary. The runtime checks a post with them before the view's object posts it (src/client/data-host.ts).
 */
export const POST_LIMITS = { text: 1_000, mention: 20, summary: 120 };

/**
 * Why a view can't be posted in a message as it is (docs/views.md#15-views-in-messages), or null. Anyone in a chat may post
 * one, so for now it keeps to its page and its records: no origins to reach, no server code, triggers, or runners, and no
 * queries beyond its collections, which its viewers read anyway. A view that needs more is added to the chat by its admins.
 * A question's view (`question`, docs/agent.md#asking-you) and a session's (`session`) keep to the same, and read no other
 * views either, since they are in no chat.
 */
export function postedProblem(
  manifest: Manifest,
  place: "message" | "question" | "session" = "message",
): string | null {
  const declared = [
    manifest.network.length ? "network" : null,
    Object.keys(manifest.actions).length ? "actions" : null,
    Object.keys(manifest.triggers).length ? "triggers" : null,
    manifest.runners.length ? "runners" : null,
    Object.values(manifest.queries).some((query) => !("collection" in query))
      ? "queries over connectors or code"
      : null,
    place !== "message" && manifest.uses.length ? "uses" : null,
  ].filter((word): word is string => word !== null);
  if (!declared.length) return null;
  const list = declared.join(", ");
  if (place === "question")
    return `A question's view keeps to its page and its records, so it can't declare ${list}. Ask with a view without them.`;
  if (place === "session")
    return `A session's view keeps to its page and its records, so it can't declare ${list}. Show one without them.`;
  return `A view in a message keeps to its page and its records, so it can't declare ${list}. Post it without them, or have the chat's admins add it as a view.`;
}
